import { formatTime } from '../utils/dateUtils'
import { SpeakerImage } from './SpeakerImage'
import { SessionCalendarButton } from './SessionCalendarButton'
import { getSpeakerEmoji } from '../utils/speakerUtils'

export function SpeakerModal({ speaker, sessions, isOpen, onClose, onSessionClick, onExportComplete }) {
  if (!isOpen || !speaker) return null

  // Find all sessions this speaker is involved in
  const speakerSessions = sessions
    .filter(session => 
      session.speakers && 
      session.speakers.some(s => s.name === speaker.name)
    )
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleSessionClick = (sessionId) => {
    onSessionClick && onSessionClick(sessionId)
    onClose() // Close modal after clicking session
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-editions-dark-light border border-editions-purple/30 rounded-2xl 
                      max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-black/50">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-start gap-4">
            <SpeakerImage speaker={speaker} className="w-16 h-16" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-1">
                {speaker.name}
              </h2>
              <p className="text-gray-300 text-sm flex items-center gap-2">
                <span>{getSpeakerEmoji(speaker.title)}</span>
                <span>{speaker.title}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              aria-label="Close speaker details"
            >
              <span className="text-xl">✕</span>
            </button>
          </div>
        </div>

        {/* Sessions List */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <span className="text-editions-orange">📅</span>
            Sessions ({speakerSessions.length})
          </h3>

          {speakerSessions.length > 0 ? (
            <div className="space-y-3">
              {speakerSessions.map(session => (
                <div
                  key={session.id}
                  onClick={() => handleSessionClick(session.id)}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 cursor-pointer
                           hover:bg-white/10 hover:border-editions-purple/30 transition-all duration-200
                           transform hover:scale-[1.02]"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-2 leading-tight">
                        {session.title}
                      </h4>
                      <div className="text-sm text-gray-400 mb-1">
                        🕒 {formatTime(session.startTime)} - {formatTime(session.endTime)}
                      </div>
                      <div className="text-sm text-gray-400">
                        📍 {session.location}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <SessionCalendarButton
                        session={session}
                        isExpanded={false}
                        onExportComplete={onExportComplete}
                      />
                      <div className={`text-xs px-3 py-1 rounded-full font-medium ${
                        session.category === 'Dev mode' 
                          ? 'bg-editions-green/20 text-editions-green'
                          : session.category === 'Biz mode'
                          ? 'bg-editions-purple/20 text-editions-purple-light'
                          : session.category === 'Mainstage'
                          ? 'bg-editions-orange/20 text-editions-orange'
                          : 'bg-gray-600/20 text-gray-300'
                      }`}>
                        {session.category}
                      </div>
                    </div>
                  </div>
                  
                  {/* Other speakers in this session */}
                  {session.speakers && session.speakers.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="text-xs text-gray-400 mb-2">With:</div>
                      <div className="flex flex-wrap gap-2">
                        {session.speakers
                          .filter(s => s.name !== speaker.name)
                          .map((otherSpeaker, index) => (
                            <span 
                              key={index}
                              className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded-full flex items-center gap-1"
                            >
                              <span>{getSpeakerEmoji(otherSpeaker.title)}</span>
                              <span>{otherSpeaker.name}</span>
                            </span>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <span className="text-3xl block mb-3">🎤</span>
              <p>No sessions found for this speaker</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-white/10 text-center">
          <p className="text-xs text-gray-400">
            Click any session to view full details in the agenda
          </p>
        </div>
      </div>
    </div>
  )
} 